import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, User, Mail, Phone, MessageSquare, CheckCircle, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { weeklySlotApiService, WeeklySlot } from '../services/weeklySlotApi';
import { scheduleExceptionApiService, UnavailableDate } from '../services/scheduleExceptionApi';
import { appointmentApiService, AppointmentResponse } from '../services/appointmentApi';
import { useAuth } from './AuthProvider';

interface AppointmentBookingProps {
  onBack: () => void;
}

interface AppointmentData {
  subject: string;
  message: string;
  date: string;
  time: string;
  appointmentType: string;
}

const AppointmentBooking: React.FC<AppointmentBookingProps> = ({ onBack }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [submitted, setSubmitted] = useState(false);
  const [weeklySlots, setWeeklySlots] = useState<WeeklySlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [unavailableDates, setUnavailableDates] = useState<UnavailableDate[]>([]);
  const [loadingExceptions, setLoadingExceptions] = useState(true);
  
  const [appointmentData, setAppointmentData] = useState<AppointmentData>({
    subject: '',
    message: '',
    date: '',
    time: '',
    appointmentType: ''
  });
  
  const [createdAppointment, setCreatedAppointment] = useState<AppointmentResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const subjects = [
    'Développement d\'application web',
    'Création d\'application mobile',
    'Consultation en IA',
    'Refonte de site existant',
    'E-commerce / Boutique en ligne',
    'Système de gestion (ERP/CRM)',
    'Audit technique',
    'Formation équipe',
    'Maintenance et support',
    'Autre (préciser dans le message)'
  ];

  // Charger les créneaux configurés par l'admin et les exceptions
  useEffect(() => {
    const loadData = async () => {
      try {
        // Charger les créneaux et exceptions en parallèle
        const [slotsPromise, exceptionsPromise] = await Promise.allSettled([
          (async () => {
            setLoadingSlots(true);
            console.log('🔄 Chargement des créneaux hebdomadaires...');
            
            const slots = await weeklySlotApiService.getWeeklySlots();
            console.log('📅 Créneaux récupérés:', slots);
            
            const activeSlots = slots.filter(slot => slot.is_active);
            console.log('✅ Créneaux actifs:', activeSlots);
            
            setWeeklySlots(activeSlots);
            
            if (activeSlots.length === 0) {
              console.warn('⚠️ Aucun créneau actif configuré par l\'admin');
            }
          })(),
          
          (async () => {
            setLoadingExceptions(true);
            console.log('🔄 Chargement des exceptions de planning...');
            
            const exceptions = await scheduleExceptionApiService.getUnavailableDates();
            console.log('🚫 Dates indisponibles récupérées:', exceptions);
            
            setUnavailableDates(exceptions);
          })()
        ]);

        // Gérer les erreurs individuellement
        if (slotsPromise.status === 'rejected') {
          console.error('❌ Erreur lors du chargement des créneaux:', slotsPromise.reason);
          setWeeklySlots([]);
        }

        if (exceptionsPromise.status === 'rejected') {
          console.error('❌ Erreur lors du chargement des exceptions:', exceptionsPromise.reason);
          setUnavailableDates([]);
        }

      } finally {
        setLoadingSlots(false);
        setLoadingExceptions(false);
      }
    };

    loadData();
  }, []);

  // Générer les créneaux disponibles pour une date donnée
  const getAvailableTimeSlotsForDate = (dateString: string): string[] => {
    const slots = getAvailableSlotsForDate(dateString);
    return slots.map(slot => weeklySlotApiService.formatTime(slot.start_time));
  };

  // Récupérer les créneaux complets avec leurs informations
  const getAvailableSlotsForDate = (dateString: string): WeeklySlot[] => {
    if (!dateString || weeklySlots.length === 0) {
      console.log('🚫 Pas de date sélectionnée ou pas de créneaux disponibles');
      return [];
    }
    
    const date = new Date(dateString);
    // Convertir le jour JavaScript (0=Dimanche, 1=Lundi...) vers notre format (0=Lundi, 1=Mardi...)
    const jsWeekday = date.getDay();
    const weekday = jsWeekday === 0 ? 6 : jsWeekday - 1;
    
    console.log(`📅 Date sélectionnée: ${dateString}, JS weekday: ${jsWeekday}, Notre weekday: ${weekday}`);
    
    const daySlots = weeklySlots.filter(slot => slot.weekday === weekday && slot.is_active);
    console.log(`🕐 Créneaux trouvés pour ce jour:`, daySlots);
    
    const sortedSlots = daySlots.sort((a, b) => a.start_time.localeCompare(b.start_time));
    
    console.log(`⏰ Créneaux triés:`, sortedSlots);
    
    return sortedSlots;
  };

  // Récupérer les types de rendez-vous disponibles pour un créneau spécifique
  const getAppointmentTypesForTimeSlot = (timeSlot: string): string[] => {
    const slots = getAvailableSlotsForDate(selectedDate);
    const slot = slots.find(s => weeklySlotApiService.formatTime(s.start_time) === timeSlot);
    
    if (!slot) return [];
    
    switch (slot.appointment_type) {
      case 'online':
        return ['online'];
      case 'physical':
        return ['physical'];
      case 'both':
        return ['online', 'physical'];
      default:
        return [];
    }
  };

  const handleInputChange = (field: keyof AppointmentData, value: string) => {
    setAppointmentData(prev => ({ ...prev, [field]: value }));
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Jours du mois précédent
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push({
        date: prevDate.getDate(),
        isCurrentMonth: false,
        fullDate: prevDate.toISOString().split('T')[0]
      });
    }
    
    // Jours du mois actuel
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      const today = new Date();
      const isToday = currentDate.toDateString() === today.toDateString();
      const isPast = currentDate < today && !isToday;
      const fullDate = currentDate.toISOString().split('T')[0];
      const isUnavailable = scheduleExceptionApiService.isDateUnavailable(fullDate, unavailableDates);
      
      days.push({
        date: day,
        isCurrentMonth: true,
        isToday,
        isPast,
        isUnavailable,
        fullDate,
        unavailabilityInfo: isUnavailable ? scheduleExceptionApiService.getUnavailabilityInfo(fullDate, unavailableDates) : null
      });
    }
    
    // Jours du mois suivant pour compléter la grille
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const nextDate = new Date(year, month + 1, day);
      days.push({
        date: day,
        isCurrentMonth: false,
        fullDate: nextDate.toISOString().split('T')[0]
      });
    }
    
    return days;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const handleDateSelect = (fullDate: string, isPast: boolean, isUnavailable: boolean) => {
    if (isPast || isUnavailable) return;
    setSelectedDate(fullDate);
    setSelectedTime(''); // Reset time selection
    setAppointmentData(prev => ({ ...prev, date: fullDate, time: '', appointmentType: '' }));
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setAppointmentData(prev => ({ ...prev, time, appointmentType: '' })); // Reset appointment type
  };

  const handleAppointmentTypeSelect = (type: string) => {
    setAppointmentData(prev => ({ ...prev, appointmentType: type }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      
      // Préparer les données pour l'API
      const apiData = appointmentApiService.prepareAppointmentData(appointmentData);
      
      // Valider les données
      const errors = appointmentApiService.validateAppointmentData(apiData);
      if (errors.length > 0) {
        setSubmitError('Erreurs de validation:\n' + errors.join('\n'));
        return;
      }
      
      // Créer le rendez-vous
      const response = await appointmentApiService.createAppointment(apiData);
      
      setCreatedAppointment(response);
      setSubmitted(true);
      
      console.log('✅ Rendez-vous créé avec succès:', response);
      
    } catch (error: any) {
      console.error('❌ Erreur lors de la création du rendez-vous:', error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getIrisMessage = (currentStep: number) => {
    const messages = {
      1: {
        title: "Planifions votre rendez-vous ! 📅",
        message: "Choisissez la date et l'heure qui vous conviennent le mieux. Je vous propose des créneaux disponibles pour une consultation personnalisée."
      },
      2: {
        title: "Parlez-moi de votre projet ! 💼",
        message: "Donnez-moi quelques détails sur vous et votre projet. Cela m'aidera à préparer notre entretien et à vous proposer les meilleures solutions."
      }
    };
    return messages[currentStep as keyof typeof messages];
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-ice flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-8 shadow-xl max-w-lg w-full"
        >
          {/* Header avec logo */}
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-elegant text-white-gold mb-2">
              Iris Softech
            </h1>
            <p className="text-sm text-gray-600">Confirmation de rendez-vous</p>
          </div>

          {/* Avatar Iris avec message de succès */}
          <div className="flex items-start space-x-4 mb-8">
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-green-200 shadow-lg">
                  <img 
                    src="/irisavatar.jpg" 
                    alt="Iris IA" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
            
            <div className="flex-1">
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 relative">
                <div className="absolute left-0 top-4 transform -translate-x-2">
                  <div className="w-4 h-4 bg-gradient-to-r from-green-50 to-blue-50 rotate-45 border-l border-b border-green-100"></div>
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  🎉 Rendez-vous confirmé !
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {createdAppointment ? 
                    appointmentApiService.generateConfirmationMessage(createdAppointment.appointment) :
                    `Parfait ! Votre rendez-vous est programmé pour le ${formatDate(appointmentData.date)} à ${appointmentData.time}. Je vous enverrai un rappel par email 24h avant notre rencontre.`
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Détails du rendez-vous */}
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <h4 className="font-semibold text-gray-900 mb-4">📋 Détails de votre rendez-vous :</h4>
            <div className="space-y-3">
              {createdAppointment && (
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 text-xs">#</span>
                  </div>
                  <span className="text-sm text-gray-700">ID: {createdAppointment.appointment.id}</span>
                </div>
              )}
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-gray-700">
                  {createdAppointment ? 
                    appointmentApiService.formatDate(createdAppointment.appointment.date) : 
                    formatDate(appointmentData.date)
                  }
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-gray-700">
                  {createdAppointment ? 
                    `${appointmentApiService.formatTime(createdAppointment.appointment.start_time)} - ${appointmentApiService.formatTime(createdAppointment.appointment.end_time)}` : 
                    appointmentData.time
                  }
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-gray-700">
                  {createdAppointment ? createdAppointment.appointment.subject : appointmentData.subject}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                {(createdAppointment ? createdAppointment.appointment.appointment_type : appointmentData.appointmentType) === 'online' ? (
                  <>
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-xs">💻</span>
                    </div>
                    <span className="text-sm text-gray-700">Rendez-vous en ligne</span>
                  </>
                ) : (
                  <>
                    <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-xs">🏢</span>
                    </div>
                    <span className="text-sm text-gray-700">Rendez-vous physique</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={onBack}
            className="btn-primary w-full"
          >
            Retour à l'accueil
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ice">
      {/* Header avec logo Iris Softech */}
      <div className="bg-white shadow-sm border-b">
        <div className="container-max py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour à l'accueil</span>
            </button>
            
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-elegant text-white-gold">
                Iris Softech
              </h1>
              <p className="text-sm text-gray-600 mt-1">Prise de Rendez-vous</p>
            </div>
            
            <div className="text-right">
              <div className="text-sm font-medium text-gray-700">Étape {step}/2</div>
              <div className="text-xs text-gray-500">Planification: {Math.round((step / 2) * 100)}%</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-max py-8">
        <div className="max-w-6xl mx-auto">
          
          {/* Message d'Iris avec avatar */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-100">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-white shadow-lg">
                      <img 
                        src="/irisavatar.jpg" 
                        alt="Iris IA" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-full border-2 border-white flex items-center justify-center">
                      <Calendar className="w-3 h-3 text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="bg-white rounded-xl p-4 shadow-sm relative">
                    <div className="absolute left-0 top-4 transform -translate-x-2">
                      <div className="w-4 h-4 bg-white rotate-45 border-l border-b border-gray-100"></div>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {getIrisMessage(step).title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {getIrisMessage(step).message}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Barre de progression */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">Progression de la réservation</span>
                <span className="text-sm text-blue-600 font-semibold">{Math.round((step / 2) * 100)}% complété</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <motion.div 
                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${(step / 2) * 100}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </div>
              
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span className={step >= 1 ? 'text-blue-600 font-medium' : ''}>Date & Heure</span>
                <span className={step >= 2 ? 'text-blue-600 font-medium' : ''}>Informations</span>
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-8"
              >
                {/* Calendrier */}
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Choisir une date</h2>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => navigateMonth('prev')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <ChevronLeft className="w-5 h-5 text-gray-600" />
                      </button>
                      <h3 className="text-lg font-semibold text-gray-800 min-w-[200px] text-center">
                        {currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                      </h3>
                      <button
                        onClick={() => navigateMonth('next')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <ChevronRight className="w-5 h-5 text-gray-600" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Jours de la semaine */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map((day) => (
                      <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Grille du calendrier */}
                  <div className="grid grid-cols-7 gap-1">
                    {getDaysInMonth(currentMonth).map((day, index) => (
                      <button
                        key={index}
                        onClick={() => handleDateSelect(day.fullDate, day.isPast || false, day.isUnavailable || false)}
                        disabled={day.isPast || day.isUnavailable}
                        title={day.isUnavailable ? scheduleExceptionApiService.getUnavailabilityMessage(day.fullDate, unavailableDates) : ''}
                        className={`
                          aspect-square flex items-center justify-center text-sm rounded-lg transition-all duration-200 relative
                          ${!day.isCurrentMonth ? 'text-gray-300' : ''}
                          ${day.isPast ? 'text-gray-300 cursor-not-allowed' : ''}
                          ${day.isUnavailable ? 'bg-red-100 text-red-400 cursor-not-allowed' : ''}
                          ${!day.isPast && !day.isUnavailable ? 'hover:bg-blue-50' : ''}
                          ${day.isToday && !day.isUnavailable ? 'bg-blue-100 text-blue-600 font-semibold' : ''}
                          ${selectedDate === day.fullDate ? 'bg-blue-600 text-white font-semibold' : ''}
                          ${day.isCurrentMonth && !day.isPast && !day.isToday && !day.isUnavailable && selectedDate !== day.fullDate ? 'text-gray-700' : ''}
                        `}
                      >
                        {day.date}
                        {day.isUnavailable && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border border-white"></div>
                        )}
                      </button>
                    ))}
                  </div>
                  
                  {/* Légende du calendrier */}
                  <div className="mt-4 flex flex-wrap gap-4 text-xs">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-blue-100 rounded"></div>
                      <span className="text-gray-600">Aujourd'hui</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-blue-600 rounded"></div>
                      <span className="text-gray-600">Sélectionné</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-red-100 rounded relative">
                        <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                      </div>
                      <span className="text-gray-600">Indisponible</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-gray-200 rounded"></div>
                      <span className="text-gray-600">Passé</span>
                    </div>
                  </div>
                  
                  {selectedDate && (
                    <div className="mt-4 space-y-2">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Date sélectionnée :</strong> {formatDate(selectedDate)}
                        </p>
                      </div>
                      
                    </div>
                  )}
                </div>

                {/* Créneaux horaires */}
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Choisir un créneau</h2>
                  
                  {!selectedDate ? (
                    <div className="text-center py-12">
                      <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">Sélectionnez d'abord une date</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Créneaux disponibles</h3>
                        {loadingSlots ? (
                          <div className="text-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="text-sm text-gray-500 mt-2">Chargement des créneaux configurés par l'admin...</p>
                          </div>
                        ) : weeklySlots.length === 0 ? (
                          <div className="text-center py-8">
                            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                            <p className="text-gray-500 mb-2">Aucun créneau configuré par l'administrateur</p>
                            <p className="text-xs text-gray-400">Contactez l'administrateur pour configurer les créneaux de rendez-vous</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-3 gap-3">
                            {getAvailableTimeSlotsForDate(selectedDate).length > 0 ? (
                              getAvailableTimeSlotsForDate(selectedDate).map((time: string) => (
                                <button
                                  key={time}
                                  onClick={() => handleTimeSelect(time)}
                                  className={`
                                    p-3 text-sm rounded-lg border transition-all duration-200
                                    ${selectedTime === time 
                                      ? 'bg-blue-600 text-white border-blue-600' 
                                      : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                                    }
                                  `}
                                >
                                  {time}
                                </button>
                              ))
                            ) : (
                              <div className="col-span-3 text-center py-8">
                                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                                <p className="text-gray-500">Aucun créneau disponible pour cette date</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {selectedTime && (
                        <div className="space-y-3">
                          <div className="p-3 bg-green-50 rounded-lg">
                            <p className="text-sm text-green-800">
                              <strong>Créneau sélectionné :</strong> {selectedTime}
                            </p>
                          </div>
                          
                          {/* Sélection du type de rendez-vous */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Type de rendez-vous</h4>
                            <div className="grid grid-cols-1 gap-2">
                              {getAppointmentTypesForTimeSlot(selectedTime).map((type) => (
                                <button
                                  key={type}
                                  onClick={() => handleAppointmentTypeSelect(type)}
                                  className={`
                                    p-3 text-sm rounded-lg border transition-all duration-200 text-left
                                    ${appointmentData.appointmentType === type 
                                      ? 'bg-blue-600 text-white border-blue-600' 
                                      : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                                    }
                                  `}
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="flex-shrink-0">
                                      {type === 'online' ? (
                                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                          <span className="text-green-600 text-sm">💻</span>
                                        </div>
                                      ) : (
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                          <span className="text-blue-600 text-sm">🏢</span>
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <p className="font-medium">
                                        {type === 'online' ? 'Rendez-vous en ligne' : 'Rendez-vous physique'}
                                      </p>
                                      <p className="text-xs opacity-75">
                                        {type === 'online' 
                                          ? 'Visioconférence via Google Meet ou Zoom' 
                                          : 'Dans nos locaux ou chez vous'
                                        }
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Message pour utilisateurs non connectés */}
                  {!isAuthenticated && (
                    <div className="mt-6 mb-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-yellow-800">
                          Veuillez vous connecter{' '}
                          <button
                            onClick={() => navigate('/login')}
                            className="text-blue-600 font-semibold underline hover:text-blue-800 transition-colors"
                          >
                            ici
                          </button>
                          {' '}pour prendre un rendez-vous avec un de nos experts.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end mt-8">
                    <button
                      onClick={() => setStep(2)}
                      disabled={!isAuthenticated || !selectedDate || !selectedTime || !appointmentData.appointmentType}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      title={!isAuthenticated ? 'Veuillez vous connecter pour continuer' : ''}
                    >
                      Continuer
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white rounded-2xl p-8 shadow-lg max-w-4xl mx-auto"
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-8">Détails du rendez-vous</h2>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sujet du rendez-vous *
                  </label>
                  <select
                    value={appointmentData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    className="input-field w-full"
                    required
                  >
                    <option value="">Sélectionnez le sujet de votre rendez-vous</option>
                    {subjects.map((subject) => (
                      <option key={subject} value={subject}>{subject}</option>
                    ))}
                  </select>
                </div>
                
                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    💬 Message pour l'administrateur
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Partagez des détails sur votre projet, vos besoins spécifiques ou toute information qui pourrait aider l'équipe à mieux préparer votre rendez-vous.
                  </p>
                  <textarea
                    value={appointmentData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    className="input-field w-full h-32 resize-none border-2 border-blue-200 focus:border-blue-500"
                    placeholder="Exemple: Je souhaite développer une application mobile pour mon restaurant. J'aimerais discuter des fonctionnalités, du budget et des délais..."
                  />
                  <div className="mt-2 text-xs text-gray-400">
                    Ce message sera visible par l'équipe IRIS SOFTECH pour mieux vous accompagner.
                  </div>
                </div>
                
                {/* Récapitulatif */}
                <div className="bg-gray-50 rounded-xl p-6 mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">📅 Récapitulatif de votre rendez-vous</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      <span className="text-gray-700">{formatDate(selectedDate)}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-blue-600" />
                      <span className="text-gray-700">{selectedTime}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      {appointmentData.appointmentType === 'online' ? (
                        <>
                          <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                            <span className="text-green-600 text-xs">💻</span>
                          </div>
                          <span className="text-gray-700">En ligne</span>
                        </>
                      ) : (
                        <>
                          <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 text-xs">🏢</span>
                          </div>
                          <span className="text-gray-700">Physique</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Afficher le message s'il y en a un */}
                  {appointmentData.message && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                      <div className="flex items-start space-x-2">
                        <MessageSquare className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-blue-900 mb-1">Votre message :</p>
                          <p className="text-sm text-blue-800">{appointmentData.message}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Affichage des erreurs */}
                {submitError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-5 h-5 text-red-500">⚠️</div>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-red-800 mb-1">Erreur</h4>
                        <p className="text-sm text-red-700 whitespace-pre-line">{submitError}</p>
                      </div>
                      <button
                        onClick={() => setSubmitError(null)}
                        className="flex-shrink-0 text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <button
                    onClick={() => setStep(1)}
                    className="btn-secondary"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!appointmentData.subject || isSubmitting}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Confirmation en cours...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>Confirmer le rendez-vous</span>
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default AppointmentBooking;
